/**
 * Folder Scope Picker (2026-08-11)
 *
 * Reusable modal sheet for the one storage-scope question — "What can Preston
 * use in <App>?" — per PLAN-storage-folder-permissions-strategy-20260811 §7.2.
 * Two answers: "Only folders I choose" (recommended, pre-selected) or
 * "Everything in <App>". Folder mode reveals a browser fed by the guarded
 * backend (browseStorageFolders): checkbox per folder, breadcrumb drill-down,
 * text filter. A chosen folder is a master folder — all its subfolders and
 * anything added to them later are included, and the copy says so plainly.
 * Saving calls setStorageScope (integrations.manage is enforced server-side;
 * 403s surface the server's "Ask an owner or admin" message). Dismissing
 * without saving leaves the connection on its current scope — new connections
 * stay grandfathered as "Everything", so there is no dead state.
 *
 * Used by IntegrationsPanel: opened after a storage OAuth callback and from
 * each connected storage card's scope chip.
 */

import React, { useState, useEffect } from 'react';
import {
  setStorageScope,
  type StorageScope,
  type StorageScopeFolder,
} from './ComposioClient';
import FolderBrowser from './FolderBrowser';

interface FolderScopePickerProps {
  /** Toolkit slug of the connected storage app (e.g. 'googledrive'). */
  appSlug: string;
  /** Human-readable app name for the copy (e.g. 'Google Drive'). */
  appDisplayName: string;
  /**
   * Current scope, or null when unknown. Null or grandfathered scopes
   * pre-select "Only folders I choose" (the recommended default); an explicit
   * "all" choice is respected when re-editing.
   */
  initialScope: StorageScope | null;
  /**
   * Multi-account (task #52): browse and save THIS connection instead of the
   * primary — used after "add another account". Absent = primary connection.
   */
  connectionId?: string;
  /** Called with the server-confirmed scope after a successful save. */
  onSaved: (scope: StorageScope) => void;
  /** Called when the sheet closes without saving — connection keeps its current scope. */
  onDismiss: () => void;
}

/**
 * Map an API error to user copy. apiRequest surfaces the server's error string
 * (a 403 denial reads "…Ask an owner or admin"), falling back to "HTTP <n>".
 */
function describeError(err: any): string {
  const msg = String(err?.message || '').trim();
  if (!msg || /^HTTP \d+$/i.test(msg)) {
    return /^HTTP 403$/i.test(msg)
      ? 'You do not have permission to change this. Ask an owner or admin.'
      : 'Something went wrong. Please try again.';
  }
  return msg;
}

export const FolderScopePicker: React.FC<FolderScopePickerProps> = ({
  appSlug,
  appDisplayName,
  initialScope,
  connectionId,
  onSaved,
  onDismiss,
}) => {
  // "Everything" is the default (task #55 — matches the server default and
  // the onboarding sheet, so save is never disabled on arrival); an explicit
  // folders scope re-opens in folders mode with its picks.
  const [mode, setMode] = useState<'all' | 'folders'>(
    initialScope && initialScope.mode === 'folders' ? 'folders' : 'all'
  );
  const [selected, setSelected] = useState<Record<string, StorageScopeFolder>>(() => {
    const init: Record<string, StorageScopeFolder> = {};
    (initialScope?.folders || []).forEach((f) => {
      init[f.id] = f;
    });
    return init;
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Escape closes the sheet (same as Cancel — no save)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const selectedList = Object.keys(selected).map((id) => selected[id]);

  // FolderBrowser hands back the folder already enriched with its path —
  // toggling is a plain add/remove keyed by id.
  const toggleFolder = (folder: StorageScopeFolder) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[folder.id]) delete next[folder.id];
      else next[folder.id] = folder;
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      const saved = await setStorageScope(appSlug, {
        mode,
        folders: mode === 'folders' ? selectedList : [],
      }, connectionId);
      onSaved(saved);
    } catch (err: any) {
      setSaveError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveDisabled = saving || (mode === 'folders' && selectedList.length === 0);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
        background: 'rgba(17, 24, 39, 0.45)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`What can Prest0n use in ${appDisplayName}?`}
        style={{
          background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '480px',
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 10px 30px rgba(17, 24, 39, 0.2)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1.1rem 1.25rem 0.75rem', borderBottom: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem',
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>
              What can Prest0n use in {appDisplayName}?
            </div>
            <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
              Default is full access to everything. Narrow it here now, or change it
              anytime later from the {appDisplayName} card in Integrations.
            </div>
          </div>
          <button
            onClick={onDismiss}
            title="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: '1.2rem', lineHeight: 1, padding: 0 }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1rem 1.25rem', overflowY: 'auto', flex: 1 }}>
          {initialScope?.grandfathered && (
            <div style={{
              padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
              background: 'rgba(198, 167, 94, 0.07)', border: '1px solid #E7DCC3',
              borderRadius: '8px', fontSize: '0.78rem', color: '#6b5d3f', lineHeight: 1.5,
            }}>
              This connection currently allows everything — it was set up before folder
              choices existed. Pick folders below to limit it.
            </div>
          )}

          {/* Option order matches the onboarding sheet (task #55): the
              default first, the tighter choice second — no "Recommended"
              badge fighting the pre-selected default. */}
          <label style={{
            display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
            padding: '0.7rem 0.8rem', borderRadius: '10px', cursor: 'pointer', marginBottom: '0.5rem',
            border: mode === 'all' ? '1px solid var(--brand-gold)' : '1px solid #E5E7EB',
            background: mode === 'all' ? 'rgba(198, 167, 94, 0.12)' : '#fff',
          }}>
            <input
              type="radio"
              name="prest0n-storage-scope-mode"
              checked={mode === 'all'}
              onChange={() => setMode('all')}
              style={{ marginTop: '0.2rem', cursor: 'pointer' }}
            />
            <span>
              <span style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem' }}>
                Everything in {appDisplayName}{' '}
                <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#9CA3AF' }}>(default)</span>
              </span>
              <span style={{ display: 'block', fontSize: '0.78rem', color: '#6B7280', marginTop: '0.15rem', lineHeight: 1.45 }}>
                Prest0n can use anything this connected account can see.
              </span>
            </span>
          </label>

          <label style={{
            display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
            padding: '0.7rem 0.8rem', borderRadius: '10px', cursor: 'pointer', marginBottom: '0.9rem',
            border: mode === 'folders' ? '1px solid var(--brand-gold)' : '1px solid #E5E7EB',
            background: mode === 'folders' ? 'rgba(198, 167, 94, 0.12)' : '#fff',
          }}>
            <input
              type="radio"
              name="prest0n-storage-scope-mode"
              checked={mode === 'folders'}
              onChange={() => setMode('folders')}
              style={{ marginTop: '0.2rem', cursor: 'pointer' }}
            />
            <span>
              <span style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem' }}>
                Only folders I choose{' '}
                <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#9CA3AF' }}>— for tighter control</span>
              </span>
              <span style={{ display: 'block', fontSize: '0.78rem', color: '#6B7280', marginTop: '0.15rem', lineHeight: 1.45 }}>
                Prest0n only uses the folders you pick — each one includes all its
                subfolders and anything added to them later.
              </span>
            </span>
          </label>

          {/* Folder browser — only in "Only folders I choose" mode. ONE
              shared implementation with the onboarding scope sheet
              (FolderBrowser, 2026-08-20): breadcrumb drill-down, so
              subfolders are choosable at any depth. */}
          {mode === 'folders' && (
            <FolderBrowser
              appSlug={appSlug}
              appDisplayName={appDisplayName}
              connectionId={connectionId}
              selected={selected}
              onToggle={toggleFolder}
            />
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #E5E7EB' }}>
          {saveError && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px',
              padding: '0.5rem 0.75rem', marginBottom: '0.6rem', color: '#DC2626', fontSize: '0.8rem',
            }}>
              {saveError}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button
              onClick={onDismiss}
              disabled={saving}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: '6px', fontSize: '0.85rem',
                border: '1px solid #E5E7EB', background: '#fff', color: '#4B5563',
                cursor: 'pointer', opacity: saving ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saveDisabled}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: '6px', fontSize: '0.85rem',
                border: 'none', background: 'var(--brand-gold)', color: '#fff',
                cursor: saveDisabled ? 'not-allowed' : 'pointer', opacity: saveDisabled ? 0.6 : 1,
              }}
            >
              {saving
                ? 'Saving...'
                : mode === 'folders'
                  ? `Save (${selectedList.length} folder${selectedList.length !== 1 ? 's' : ''})`
                  : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FolderScopePicker;
